import { useEffect } from 'react';
import type { Layer } from 'leaflet';
import { useMap } from 'react-leaflet';

// OpenFreeMap: free OSM-based vector tiles, commercial use allowed, no key and no request
// limit. The OSM volunteer tile servers block apps that break their usage policy (403),
// MapTiler's free plan is non-commercial only.
const STYLE_URL = 'https://tiles.openfreemap.org/styles/liberty';

const ATTRIBUTION =
  '<a href="https://openfreemap.org" target="_blank" rel="noopener">OpenFreeMap</a> ' +
  '&copy; <a href="https://www.openmaptiles.org/" target="_blank" rel="noopener">OpenMapTiles</a> ' +
  'Data from <a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noopener">OpenStreetMap</a>';

/**
 * Base map for react-leaflet maps. MapLibre (vector rendering) is loaded on demand, so it
 * stays out of the main bundle and only ships with pages that show a map.
 */
export function OpenFreeMapLayer() {
  const map = useMap();

  useEffect(() => {
    let layer: Layer | null = null;
    let cancelled = false;

    void (async () => {
      const [{ maplibreGL }, { setWorkerUrl }, { default: workerUrl }] = await Promise.all([
        import('@maplibre/maplibre-gl-leaflet'),
        import('maplibre-gl'),
        // why: MapLibre resolves its worker as a sibling file of its own module, which Vite's
        // bundling does not carry along ("Worker failed to load"). Vite bundles it here instead.
        import('maplibre-gl/dist/maplibre-gl-worker.mjs?worker&url'),
        import('maplibre-gl/dist/maplibre-gl.css'),
      ]);
      if (cancelled) return;

      setWorkerUrl(workerUrl);

      layer = maplibreGL({ style: STYLE_URL, attributionControl: false });
      layer.getAttribution = () => ATTRIBUTION;
      layer.addTo(map);
    })();

    return () => {
      cancelled = true;
      if (layer) map.removeLayer(layer);
    };
  }, [map]);

  return null;
}
